import React, { ReactNode } from 'react';
import styled from 'astroturf/react';

import { Body } from 'js/design_system/Body';
import { BundleWork } from 'js/bulk_download/work_cards_shared/types';
import { WorkCard } from 'js/bulk_download/work_cards_shared/WorkCard';
import $image_url from 'js/utils/image_url';

interface Props {
  relatedWorks: BundleWork[];
  visibleRelatedWorks: BundleWork[];
  sourceWorkPreview: BundleWork | undefined;
  showAnnotations: boolean;
  badgePlacement: 'side' | 'below' | 'above';
  previewFooter: ReactNode;
  emptyState: ReactNode;
  showBucketBadges: boolean;
  totalRelatedCount: number;
}

export function SplitVerdictPreviewList({
  visibleRelatedWorks,
  sourceWorkPreview,
  showAnnotations,
  badgePlacement,
  previewFooter,
  emptyState,
  showBucketBadges,
  totalRelatedCount,
}: Props) {
  const thumbnailSrc = sourceWorkPreview?.thumbnailUrl
    ? $image_url(sourceWorkPreview.thumbnailUrl)
    : '/images/blank-paper.jpg';

  return (
    <SplitContainer>
      {sourceWorkPreview && (
        <SourceColumn>
          <ColumnLabel>
            <Body size="xs" bold as="span">
              Your paper
            </Body>
          </ColumnLabel>
          <LargeThumbnail>
            <img alt="" src={thumbnailSrc} />
          </LargeThumbnail>
          <Body size="sm" bold as="div" style={primaryText}>
            {sourceWorkPreview.title}
          </Body>
          {sourceWorkPreview.summary && (
            <AbstractText>
              <Body size="xs" as="div">
                {sourceWorkPreview.summary}
              </Body>
            </AbstractText>
          )}
          <MetaLine>
            {sourceWorkPreview.authors && (
              <Body size="xs" as="span">
                {sourceWorkPreview.authors}
              </Body>
            )}
            {sourceWorkPreview.year && (
              <Body size="xs" as="span">
                {sourceWorkPreview.year}
              </Body>
            )}
          </MetaLine>
        </SourceColumn>
      )}
      <BundleColumn>
        <ColumnLabel>
          <Body size="xs" bold as="span">
            Your bundle · {totalRelatedCount} papers
          </Body>
        </ColumnLabel>
        <BundleScroll>
          {visibleRelatedWorks.length === 0 && emptyState ? (
            <>{emptyState}</>
          ) : (
            visibleRelatedWorks.map((work) => (
              <WorkCard
                key={work.id}
                work={work}
                compact
                showAnnotations={showAnnotations}
                badgePlacement={badgePlacement}
                showBucketBadge={showBucketBadges}
              />
            ))
          )}
        </BundleScroll>
        {previewFooter}
      </BundleColumn>
    </SplitContainer>
  );
}

const primaryText: React.CSSProperties = {
  color: 'var(--text-primary, #141413)',
};

const SplitContainer = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  gap: var(--spacing-xs-16, 16px);

  @media (max-width: 567px) {
    flex-direction: column;
  }
`;

const SourceColumn = styled.div`
  flex: 0 0 40%;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-8, 8px);
  padding: var(--spacing-xs-16, 16px);
  border-right: 1px solid var(--brand-colors-neutral-400, #dddde2);
  overflow-y: auto;

  @media (max-width: 567px) {
    flex: none;
    border-right: none;
    border-bottom: 1px solid var(--brand-colors-neutral-400, #dddde2);
    max-height: 200px;
  }
`;

const BundleColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  position: relative;
`;

const ColumnLabel = styled.div`
  color: var(--text-secondary, #535366);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding-bottom: var(--spacing-xs-8, 8px);
`;

const LargeThumbnail = styled.div`
  width: 100%;
  max-width: 180px;
  aspect-ratio: 8.5 / 11;
  border: 1px solid var(--stroke-beige-light, #ccc9bc);
  background: var(--background-white, #ffffff);
  overflow: hidden;
  border-radius: var(--border-radius-radius-sm);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const AbstractText = styled.div`
  color: var(--text-secondary, #535366);
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 6;
`;

const MetaLine = styled.div`
  display: flex;
  gap: var(--spacing-xs-8, 8px);
  color: var(--text-secondary, #535366);
`;

const BundleScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;
